import {
	Component,
	OnInit,
	OnDestroy,
	AfterViewInit,
	ViewChild,
} from '@angular/core';
import {
	BasePageComponent,
	PageSkeletonType,
} from '@app/abstracts/base-page/base-page.component';
import {
	FormationThematique,
	FormationThematiqueService,
} from '@app/models/formation-thematique';
import { Color } from '@app/models/color';
import { PageService, PageInterface } from '@app/services/page.service';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NavigationService } from '@app/services/navigation.service';
import { ErrorService } from '@app/services/error.service';
import { SessionService } from '@app/services/session.service';
import { User } from '@app/models/user';
import { Formation } from '@app/models/formation';
import { Image } from '@app/models/image';
import {
	NgSignaturePadOptions,
	SignaturePadComponent,
} from '@almothafar/angular-signature-pad';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AttestationPresenceThematiqueService } from '@app/models/attestation-presence-thematique';

@Component({
	selector: 'app-molecule-formation-thematique-signature',
	templateUrl: './molecule-formation-thematique-signature.component.html',
	styleUrls: ['./molecule-formation-thematique-signature.component.less'],
})
export class MoleculeFormationThematiqueSignatureComponent
	extends BasePageComponent
	implements OnInit, OnDestroy, AfterViewInit
{
	protected skeleton: PageSkeletonType = 'detail';
	userLogged: User;
	thematiqueId: string;
	signatureTimestamp: number;
	signaturePeriod: 'morning' | 'afternoon';
	thematique: FormationThematique;
	colorThematique: Color;
	loading = false;

	// Thematique details for header
	thematiqueDetails: {
		name: string;
		formationName: string;
		sponsor: string;
		avatarSponsor: string;
		respondantsMax: number;
		coach: string;
	};

	// Signature pad
	@ViewChild('signature')
	public signaturePad: SignaturePadComponent;
	public signaturePadOptions: NgSignaturePadOptions = {
		canvasWidth: 500,
		canvasHeight: 224,
	};
	public unsubscribe$ = new Subject<void>();
	readyToSave = false;
	processing = false;

	// Nombre minimum de pixels dessinés pour valider la signature.
	// Objectif : écarter le simple point sans gêner une vraie signature.
	private readonly MIN_SIGNATURE_PIXELS = 100;
	// Message d'erreur affiché lorsque la signature est jugée trop pauvre.
	signatureError: string = null;

	constructor(
		private sessionService: SessionService,
		protected pageService: PageService,
		protected translate: TranslateService,
		private formationThematiqueService: FormationThematiqueService,
		private route: ActivatedRoute,
		private router: Router,
		private navigate: NavigationService,
		private errorService: ErrorService,
		private breakpointObserver: BreakpointObserver,
		private attestationPresenceThematiqueService: AttestationPresenceThematiqueService
	) {
		super(pageService, translate);
	}

	async ngOnInit() {
		super.ngOnInit();
		this.thematiqueId = this.route.snapshot.params.id;
		// Get timestamp from URL
		const timestampParam = this.route.snapshot.params.date;
		console.log('URL params:', this.route.snapshot.params);
		console.log('Timestamp param from URL:', timestampParam);
		console.log('Timestamp param type:', typeof timestampParam);
		this.signatureTimestamp = parseInt(timestampParam, 10);
		console.log('Parsed timestamp:', this.signatureTimestamp);
		// Convert French period from URL to English internally
		const periodParam = this.route.snapshot.params.period;
		this.signaturePeriod = this.periodFromFrench(periodParam);

		await this.refresh();
	}

	private periodFromFrench(
		periodFr: string
	): 'morning' | 'afternoon' | undefined {
		if (periodFr === 'matin') {
			return 'morning';
		} else if (periodFr === 'apres-midi') {
			return 'afternoon';
		}
		return undefined;
	}

	ngAfterViewInit() {
		super.ngAfterViewInit();
		// Setup responsive signature pad
		this.breakpointObserver
			.observe(['(min-width: 768px)'])
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((state: BreakpointState) => {
				if (state.matches) {
					// Desktop
					this.signaturePad.set('canvasWidth', 500);
					this.signaturePad.set('canvasHeight', 224);
				} else {
					// Mobile
					this.signaturePad.set('canvasWidth', 280);
					this.signaturePad.set('canvasHeight', 180);
				}
				this.signaturePad.clear();
			});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	async refresh() {
		this.loading = true;
		this.userLogged = await this.sessionService.getCachedUser();
		this.thematique = await this.getThematique();

		if (this.thematique) {
			this.colorThematique = <Color>this.thematique.props.color;
			this.thematiqueDetails = this.extractThematiqueDetails();
		}

		this.refreshPageData();
		this.loading = false;
	}

	getThematique(): Promise<FormationThematique> {
		return this.formationThematiqueService
			.get(this.thematiqueId, {
				_populate: [
					'color',
					'formation.sponsor.avatar',
					'formateur',
					'formation.respondants',
				],
			})
			.catch((err) => {
				this.errorService.handle(err);
				this.navigate.go(['/session']);
				return null;
			});
	}

	extractThematiqueDetails() {
		const formation = <Formation>this.thematique.props.formation;
		const sponsor = <User>formation.props.sponsor;
		const coach = <User>this.thematique.props.formateur;

		return {
			name: this.thematique.getLabel(),
			formationName: formation.props.name,
			sponsor: sponsor ? sponsor.getLabel() : '',
			avatarSponsor: sponsor
				? sponsor.avatarExists()
					? (<Image>sponsor.props.avatar).getUrl()
					: null
				: null,
			respondantsMax: formation.props?.respondants?.length || 0,
			coach: coach ? coach.getLabel() : '',
		};
	}

	getPageData(): PageInterface {
		const breadcrumbs: {
			label: string;
			link?: string[];
		}[] = [
			{
				label: this.translate.instant(
					'top_bar-formation_thematique_list'
				),
				link: ['/session/list'],
			},
			{
				label: this.thematique ? this.thematique.getLabel() : null,
				link: [
					`/session/${
						this.thematique ? this.thematique.getId() : null
					}/details`,
				],
			},
			{
				label: 'Signature de présence',
			},
		];

		return {
			title: this.translate.instant(
				'page_title-formation_thematique_signature',
				{
					thematique: this.thematique
						? this.thematique.getLabel()
						: '',
				}
			),
			breadcrumbs: breadcrumbs,
			hideTopBar: false,
			hideMenu: false,
		};
	}

	navigateBack(): void {
		this.router.navigate(['/session', this.thematiqueId, 'details']);
	}

	getFormattedDate(): string {
		if (!this.signatureTimestamp) {
			return '';
		}
		const date = new Date(this.signatureTimestamp);
		return date.toLocaleDateString('fr-FR', {
			day: 'numeric',
			month: 'long',
			year: 'numeric',
		});
	}

	getPeriodLabel(): string {
		if (!this.signaturePeriod) {
			return '';
		}
		return this.signaturePeriod === 'morning' ? 'Matin' : 'Après-midi';
	}

	drawComplete(event: MouseEvent | Touch): void {
		// Called when user finishes drawing
		const drawnPixels = this.countDrawnPixels();

		if (drawnPixels < this.MIN_SIGNATURE_PIXELS) {
			// Trop peu de pixels : probablement un simple point, on refuse.
			this.readyToSave = false;
			this.signatureError =
				'Votre signature semble trop simple (un point ne suffit pas). Merci d’apposer une vraie signature.';
			return;
		}

		this.readyToSave = true;
		this.signatureError = null;
	}

	/**
	 * Compte le nombre de pixels effectivement dessinés sur le canvas
	 * (pixels dont le canal alpha est non nul).
	 */
	private countDrawnPixels(): number {
		if (!this.signaturePad) {
			return 0;
		}
		const canvas = this.signaturePad.getCanvas();
		if (!canvas) {
			return 0;
		}
		const context = canvas.getContext('2d');
		if (!context) {
			return 0;
		}

		const imageData = context.getImageData(
			0,
			0,
			canvas.width,
			canvas.height
		);
		const data = imageData.data;
		let count = 0;
		// Le canal alpha est le 4e octet de chaque pixel (RGBA).
		for (let i = 3; i < data.length; i += 4) {
			if (data[i] > 0) {
				count++;
			}
		}

		// signature_pad agrandit le canvas selon le devicePixelRatio (x2 sur
		// les écrans Retina) : on normalise le compte en pixels "CSS" pour que
		// le seuil reste cohérent quel que soit l'écran.
		const ratio =
			typeof window !== 'undefined' && window.devicePixelRatio
				? window.devicePixelRatio
				: 1;
		return Math.round(count / (ratio * ratio));
	}

	clearSignature(): void {
		this.signaturePad.clear();
		this.readyToSave = false;
		this.signatureError = null;
	}

	async submitSignature(): Promise<void> {
		if (!this.readyToSave || !this.signaturePeriod) {
			return;
		}

		this.readyToSave = false;
		this.processing = true;

		try {
			const signatureBase64 = this.signaturePad.toDataURL();

			const isFormateur = this.userLogged.props.role === 'coach';
			if (isFormateur) {
				await this.attestationPresenceThematiqueService.signPresenceAsFormateur(
					this.thematiqueId,
					this.signatureTimestamp,
					this.signaturePeriod,
					signatureBase64
				);
			} else {
				await this.attestationPresenceThematiqueService.signPresence(
					this.thematiqueId,
					this.signatureTimestamp,
					this.signaturePeriod,
					signatureBase64
				);
			}

			// Redirect to thematique details page
			this.router.navigate(['/session', this.thematiqueId, 'details']);
		} catch (err) {
			this.errorService.handle(err);
			this.processing = false;
			this.readyToSave = true;
		}
	}
}
